/** @jsxImportSource @emotion/react */
import { createStyles, useTheme } from '@quickbit/qb-design-system';
import { PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Outlet } from 'react-router-dom';
import { useMedia } from 'react-use';
import { useNavigateWithSnapshots } from 'context';

const Mobile = ({ children }: PropsWithChildren) => {
  const { breakpoints } = useTheme();
  const isDesktop = useMedia(breakpoints.laptopSmall.replace('@media ', ''));

  if (isDesktop) return null;
  return children;
};

/**
 * Should be uses with a Route that has index={true}
 */
const Desktop = ({ children }: PropsWithChildren) => {
  const { breakpoints } = useTheme();
  const isDesktop = useMedia(breakpoints.laptopSmall.replace('@media ', ''));

  if (!isDesktop) return <Outlet />;
  return (
    <>
      {children}
      <Outlet />
    </>
  );
};

interface Props {
  children?: ReactNode;
  onCloseNavigate?: string | number;
}

const DesktopModal = ({ children, onCloseNavigate }: Props) => {
  const styles = useStyles();
  const navigate = useNavigateWithSnapshots();
  const { breakpoints } = useTheme();
  const isDesktop = useMedia(breakpoints.laptopSmall.replace('@media ', '')); // TODO: put in DS
  const [modalRoot, setModalRoot] = useState<HTMLElement>();
  useEffect(() => {
    const root = document.getElementById('modal-root');
    if (!root) {
      throw Error(
        'div [id=modal-root] not found. Have you forgotten DesignSystemProvider'
      );
    }
    setModalRoot(root);
  }, []);

  if (!modalRoot) {
    return null;
  }

  return (
    <>
      {!isDesktop && children}
      {isDesktop &&
        createPortal(
          <>
            <div css={[styles.spanAll, styles.backdrop]} />
            <div
              css={[styles.spanAll]}
              onClick={() =>
                onCloseNavigate !== undefined &&
                navigate(onCloseNavigate as string, 'none')
              }
            >
              <div
                css={styles.modal}
                id="MODAL"
                onClick={(e) => e.stopPropagation()}
              >
                {children}
              </div>
            </div>
          </>,
          modalRoot
        )}
    </>
  );
};

const useStyles = createStyles(({ palette, sizing, breakpoints, shape }) => ({
  spanAll: {
    position: 'fixed',
    top: 'var(--safe-area-inset-top)',
    right: 'var(--safe-area-inset-right)',
    bottom: 'var(--safe-area-inset-bottom)',
    left: 'var(--safe-area-inset-left)',
    zIndex: 100,
    [breakpoints.laptopSmall]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  backdrop: {
    background: `${palette.grey.x600}69`,
    backdropFilter: 'blur(3px)',
  },
  modal: {
    background: palette.grey.x50,
    height: '100%',
    [breakpoints.laptopSmall]: {
      height: 'unset',
      minWidth: sizing.medium,
      maxWidth: sizing.large,
      borderRadius: shape.borderRadius,
      overflow: 'hidden',
    },
  },
}));

Mobile.displayName = 'LayoutRoute.Mobile';
Desktop.displayName = 'LayoutRoute.Desktop';
DesktopModal.displayName = 'LayoutRoute.DesktopModal';

export const LayoutRoute = { Mobile, Desktop, DesktopModal };
